/* eslint-disable unused-imports/no-unused-vars */
// @ts-strict-ignore

export type ToastType = "success" | "warning" | "error";

import { Subject, Subscription } from 'rxjs';
import sanitizeHtml from 'sanitize-html';
import { I18nService } from 'rainbow-web-sdk/services/core/i18n.service';
import { RainbowNotification, RainbowNotificationAction } from '@ale-rainbow/rainbow-notification';
import { ProfileService } from 'rainbow-web-sdk/services/core/profile.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { TelephonyService } from 'rainbow-web-sdk/services/telephony/telephony.service';
import { SettingsService } from 'rainbow-web-sdk/services/core/settings.service';
import { RingingService } from 'rainbow-web-sdk/services/ringing/ringing.service';
import { EmojiService } from 'rainbow-web-sdk/services/emoji.service';
import { WebrtcP2PService } from 'rainbow-web-sdk/services/webrtc/webrtcP2P.service';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { isDarkTheme, isDesktop } from 'rainbow-web-sdk/utils/browser';
import { Service } from 'rainbow-web-sdk/services/service';
import { RainbowSDK } from 'rainbow-web-sdk/rainbow.sdk';
import { EventService, EventServiceEvents } from '../core';

export const NOTIF_VIEWER_SVC = 'NotificationViewerService';

export class NotificationsViewerService extends Service {

    public callNotifVisible: boolean = false;
    private rainbowNotifications: RainbowNotification[] = [];
    private rainbowNotifSubject: Subject<any> = new Subject<any>();

    private readonly logger = LoggerService.getInstance();
    private readonly i18n = I18nService.getInstance();
    private readonly ringingService = RingingService.getInstance();
    private readonly telephonyService = TelephonyService.getInstance();
    private readonly profileService = ProfileService.getInstance();
    private readonly settingsService = SettingsService.getInstance();
    private readonly webrtcP2PService = WebrtcP2PService.getInstance();
    private readonly contactService = ContactService.getInstance();
    private readonly emojiService = EmojiService.getInstance();
    private readonly eventService = EventService.getInstance();
    private readonly urgencyLabels = { std: null, low: 'notifyAlert', middle: 'warningAlert', high: 'emergencyAlert' };

    public static getInstance() { return Service.create(NOTIF_VIEWER_SVC, NotificationsViewerService); }
    public static build() { return new NotificationsViewerService(); }
    private constructor() { super(); }

    /* ----------------------------------------------------------- */
    /* RAINBOW NOTIFICATIONS                                       */
    /* ----------------------------------------------------------- */
    public emitNotification(notifConfig: any): RainbowNotification {
        this.logger.debug(`[NotificationsViewerService] emitNotification ${notifConfig.id}`);

        if (notifConfig?.type === 'call' && notifConfig?.message) {
            //for web notifications, add original message
            notifConfig.originalMessage = notifConfig.message;
            notifConfig.message = sanitizeHtml(notifConfig.message, {
                allowedTags: sanitizeHtml.defaults.allowedTags,
                allowedAttributes: sanitizeHtml.defaults.allowedAttributes,
                allowedSchemes: ['http', 'https', 'mailto', 'tel']
            });
        }

        if (notifConfig?.type === 'call' && notifConfig?.mdMessage) {
            notifConfig.mdMessage = RainbowSDK.getInstance().markdownParser?.markdownToHtml(notifConfig.mdMessage);
        }

        // Handle special case of /md message
        if (notifConfig.message.startsWith("/md")) {
            notifConfig.message = notifConfig.message.slice(4);
            notifConfig.message = RainbowSDK.getInstance().markdownParser?.markdownToHtml(notifConfig.message).replace(/(<([^>]+)>)/gi, "");
        }

        const rainbowNotification = RainbowNotification.create(notifConfig);
        rainbowNotification.isDarkTheme = isDarkTheme();
        if (notifConfig.roomName) {
            rainbowNotification.message = rainbowNotification.origin.trim() + ": " + rainbowNotification.message;
            rainbowNotification.origin = notifConfig.roomName;
        }

        if (notifConfig.originalMessage) {
            rainbowNotification["originalMessage"] = notifConfig.originalMessage;
        }

        //update the duration of the call in case of settings
        if (notifConfig.type === "info") {

            const notificationDurationValue = this.settingsService.getSetting("notificationDurationValue");
            if (notificationDurationValue && notificationDurationValue !== "3" && notifConfig.delay <= 5000) {
                rainbowNotification.delay = parseInt(notificationDurationValue, 10) * 1000;
            }

            //for desktop app, add close button to all notifications
            if (isDesktop() && !notifConfig.noClose) {
                rainbowNotification.close = true;
                if (!rainbowNotification.actions?.length) { rainbowNotification.actions = []; }
                rainbowNotification.actions.push(RainbowNotificationAction.create({ name: 'closeButton', handler: () => { } }));
            }
        }

        this.translateNotification(rainbowNotification);

        const index = this.getRainbowNotificationIndexById(notifConfig.id);
        if (index !== -1) { this.rainbowNotifications[index] = rainbowNotification; }
        else { this.rainbowNotifications.push(rainbowNotification); }

        //CR22332++
        if (rainbowNotification && rainbowNotification.type === "call" && rainbowNotification.actions) {
            if (rainbowNotification.actions.find(elt => elt.name === "answerPhone")) {
                const imp = rainbowNotification.actions.map((elt) => {
                    let ret = "[name:" + elt.name + "/handlerNull:" + (elt.handler === null);
                    if (elt.handler && elt.name === "answerPhone") {
                        const funcExtract = String(elt.handler).replace(/\s+/g, '').trim().slice(0, 20);
                        ret = ret + "/handler:" + funcExtract + " ...";
                    }
                    return ret + "]";
                });
                this.logger.debug("[NotificationsViewerService] emitNotification for CR22332 -- actions table handler : " + imp);
            }
        }//CR22332--

        this.checkVisibility();
        if (isDesktop()) {
            if (rainbowNotification.avatarURL) {
                // Not the most beautiful or optimized code ... Alternative is 1 fct including each translation ?
                rainbowNotification.avatarURL = this.replaceRelativeUnknowUserUrl(rainbowNotification.avatarURL);
                rainbowNotification.avatarURL = this.replaceRelativeDefaultChannelUrl(rainbowNotification.avatarURL);
                rainbowNotification.avatarURL = this.replaceRelativeDefaultLogoRainbowUrl(rainbowNotification.avatarURL);
                rainbowNotification.avatarURL = this.replaceRainbowRoomUrl(rainbowNotification.avatarURL);
                rainbowNotification.avatarURL = this.replaceHuntingGroupUrl(rainbowNotification.avatarURL);
                rainbowNotification.avatarURL = this.replace3PCCMakecallUrl(rainbowNotification.avatarURL);
                rainbowNotification.avatarURL = this.replace3PCCCallbackUrl(rainbowNotification.avatarURL);
            }
            if (rainbowNotification.avatarURLs) {
                rainbowNotification.avatarURLs.forEach((avatarUrl: string) => {
                    avatarUrl = this.replaceRelativeUnknowUserUrl(avatarUrl);
                    avatarUrl = this.replaceRelativeDefaultChannelUrl(avatarUrl);
                    avatarUrl = this.replaceRainbowRoomUrl(avatarUrl);
                });
            }
            rainbowNotification.iconPath = `${window.location.origin}/rb/${version}/assets/images`;
        }

        if (index !== -1) { this.rainbowNotifSubject.next({ action: 'modify', notif: rainbowNotification }); }
        else { this.rainbowNotifSubject.next({ action: 'add', notif: rainbowNotification }); }

        if (rainbowNotification.urgency === 'high' && this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.ALERT_NOTIFICATIONS_ALLOWED)) {
            rainbowNotification.infoHandler();
        }

        // Handle remove from rainbowNotifications array for desktop 500ms after real timeout
        if (rainbowNotification.delay && isDesktop()) {

            // Note: We re-use the delayTimeout property from RainbowNotification to store the setTimeout id for clear
            // It SHOULD work as, we are on desktop so thiis is not used by displayNotification to store its timeout
            rainbowNotification.delayTimeout = setTimeout(() => {
                const index = this.getRainbowNotificationIndexById(rainbowNotification?.id);
                if (index > -1) {
                    if (this.rainbowNotifications[index]?.urgency === 'high') { this.ringingService.stopUrgencyNotif(); }
                    this.rainbowNotifications?.splice(index, 1);
                }
            }, rainbowNotification?.delay + 500);
        }

        return rainbowNotification;
    }

    private replaceRelativeUnknowUserUrl(url: string): string {
        if (url.indexOf('unknownUser.jpeg') !== -1) {
            url = `${window.location.origin}/assets/skins/rainbow/images/conversations/unknownUser.jpeg`;
        }
        return url;
    }

    private replaceRainbowRoomUrl(url: string): string {
        if (url.indexOf('androidtv.svg') !== -1) {
            url = `${window.location.origin}/assets/skins/rainbow/images/misc/androidtv.svg`;
        }
        return url;
    }

    private replaceRelativeDefaultChannelUrl(url: string): string {
        if (url.indexOf('default_channel_avatar.png') !== -1) {
            url = `${window.location.origin}${url}`;
        }
        return url;
    }

    private replaceRelativeDefaultLogoRainbowUrl(url: string): string {
        if (url.indexOf('favicon2-32x32.png') !== -1) {
            url = `${window.location.origin}${url}`;
        }
        return url;
    }

    private replaceHuntingGroupUrl(url: string): string {
        if (url.indexOf('logo-offer-voice.svg') !== -1) {
            url = `${window.location.origin}${url}`;
        }
        return url;
    }

    private replace3PCCMakecallUrl(url: string): string {
        if (url.indexOf('3pccmakecall.png') !== -1) {
            url = `${window.location.origin}${url}`;
        }
        return url;
    }

    private replace3PCCCallbackUrl(url: string): string {
        if (url.indexOf('3pcccallback.png') !== -1) {
            url = `${window.location.origin}${url}`;
        }
        return url;
    }

    public hideNotification(id: string): void {
        this.logger.debug(`[NotificationsViewerService] hideNotification ${id}`);

        const index = this.getRainbowNotificationIndexById(id);
        if (index > -1) {
            const rainbowNotification = this.rainbowNotifications[index];
            if (rainbowNotification.urgency === 'high') { this.ringingService.stopUrgencyNotif(); }
            this.rainbowNotifications.splice(index, 1);
            this.checkVisibility();
            this.rainbowNotifSubject.next({ action: 'remove', notifId: id });
        }

    }

    public hideStopRingingNotification(id: string): void {
        const index = this.getRainbowNotificationIndexById(id);
        if (index > -1) {
            this.ringingService.stopIncomingRinging();
            this.ringingService.stopSecondCallTone();
            const notTeamsV3Flag = !this.contactService.userContact.isRainbowForTeamsUser;//RQRAINB-9057 teamsV3 mng
            //Hide pop up except for teamsV3 (then only stop ringing)
            if (notTeamsV3Flag) {
                this.rainbowNotifications.splice(index, 1);
                this.checkVisibility();
                this.rainbowNotifSubject.next({ action: 'remove', notifId: id });
            }
            const call = this.telephonyService.calls[id];
            if (!call) { return; } //just in case but should not occur
            call.noIncomingPopup = true;

            if (call.isMediaPillarCall() && call.mediaPillarCall) { //RQRAINB-9057++
                //send a dummy reject of webrtc part to others ressource to force them to give up for the call
                const webrtcCall = call.mediaPillarCall.webrtcCallRef;
                if (webrtcCall) {
                    this.webrtcP2PService.dummyRejectCallForRemote(webrtcCall.id);
                }
            }//RQRAINB-9057--
        }
    }

    public subscribe(handler: any): Subscription { return this.rainbowNotifSubject.subscribe(handler); }

    public getRainbowNotificationIndexById(id: string): number {
        return this.rainbowNotifications.findIndex((notification: RainbowNotification) => { return notification.id === id; });
    }

    public getCallRainbowNotificationIndexById(id: string): number {
        return this.rainbowNotifications.findIndex((notification: RainbowNotification) => { return notification.id === id && notification.type === "call"; });
    }

    public getRainbowNotificationByConvId(convId: string): RainbowNotification | undefined {
        return this.rainbowNotifications.find((notification: RainbowNotification) => { return notification.convId === convId; });
    }

    private translateNotification(notif: RainbowNotification): void {
        notif.message = notif.message ? notif.message : ""; // MERDEUUUUUU
        notif.message = this.emojiService.shortnameToUnicode(notif.message);
        notif.infoTitle = notif.infoTitle ? this.i18n.translate(notif.infoTitle) : null;
        notif.infoMessage = notif.infoMessage ? this.i18n.translate(notif.infoMessage) : null;
        notif.urgencyLabel = notif.urgency && notif.urgency !== 'undefined' ? this.i18n.translate(this.urgencyLabels[notif.urgency]) : null;
        if (notif.actions) {
            notif.actions.map((action: RainbowNotificationAction) => {
                action.label = this.i18n.translate(action.label);
                action.choiceTitleLabel = action.choiceTitleLabel ? this.i18n.translate(action.choiceTitleLabel) : null;
                action.choiceCloseLabel = action.choiceCloseLabel ? this.i18n.translate(action.choiceCloseLabel) : null;
                if (action.choice) { action.choice.forEach((choice: any) => { choice.label = this.i18n.translate(choice.label); }); }
            });
        }
    }

    private checkVisibility(): void {
        this.callNotifVisible = !isDesktop() && this.rainbowNotifications.some((notif: RainbowNotification) => { return notif.type === 'call'; });
    }

    public answerFirstVisibleAudioCall(): void {
        if (this.rainbowNotifications.length === 0) { return; }
        // Find the rainbowNotification to answer
        const rainbowNotificationToAnswer = this.rainbowNotifications.find((rainbowNotif) => {
            return rainbowNotif?.actions?.some((action: RainbowNotificationAction) => {
                return (action?.name === "answer_audio") || (action?.name === "answerPhone");
            });
        });
        const answerAction = rainbowNotificationToAnswer?.actions?.find((notifAction: RainbowNotificationAction) => { return (notifAction.name === "answer_audio" || notifAction.name === "answerPhone"); });
        this.logger.info(`answerFirstVisibleAudioCall ${this.rainbowNotifications.length} / ${answerAction?.name}`);
        answerAction?.handler();
    }

    public answerFirstVisibleVideoCall(): void {
        if (this.rainbowNotifications.length === 0) { return; }
        // Find the rainbowNotification to answer
        const rainbowNotificationToAnswer = this.rainbowNotifications.find((rainbowNotif) => {
            return rainbowNotif?.actions?.some((action: RainbowNotificationAction) => {
                return action?.name === "answer_video";
            });
        });

        const answerAction = rainbowNotificationToAnswer?.actions?.find((notifAction: RainbowNotificationAction) => { return notifAction.name === "answer_video"; });
        this.logger.info(`answerFirstVisibleVideoCall ${this.rainbowNotifications.length} / ${answerAction?.name}`);
        answerAction?.handler();
    }

    public declineFirstVisibleCall(): void {
        if (this.rainbowNotifications.length === 0) { return; }
        // Find the rainbowNotification to decline
        const rainbowNotificationToDecline = this.rainbowNotifications.find((rainbowNotif) => {
            return rainbowNotif?.actions?.some((action: RainbowNotificationAction) => {
                return (action?.name === "deflectToVM") || (action?.name === "decline");
            });
        });
        const declineAction = rainbowNotificationToDecline?.actions?.find((notifAction: RainbowNotificationAction) => {
            return (notifAction.name === "deflectToVM" || notifAction.name === "decline");
        });
        this.logger.info(`KeyboardListener - declineFirstVisibleCall ${this.rainbowNotifications.length} / ${declineAction?.name}`);
        declineAction?.handler();
    }

    /* ----------------------------------------------------------- */
    /* HTML 5 NOTIFICATIONS                                        */
    /* ----------------------------------------------------------- */
    public getPermission(): string {
        if (!('Notification' in window) || !Notification) { return 'granted'; }
        return Notification.permission;
    }

    public async requestPermission() {
        if (!('Notification' in window) || !Notification) return Promise.resolve('default');
        return Notification.requestPermission();
    }


    /* ----------------------------------------------------------- */
    /* TOAST NOTIFICATIONS                                         */
    /* ----------------------------------------------------------- */
    public emitToastNotification(type: ToastType, text: string, duration: number = 3000, partialLayout: boolean = false): void {
        this.eventService.publish(EventServiceEvents.TOAST, text, type, duration, partialLayout);
    }
}
